import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import CoverageChecker from "@/components/CoverageChecker";
import {
  Signal, Wifi, Satellite, Tv, CheckCircle2, ArrowRight, Tv2,
  Zap, Repeat, Smartphone, Star, Menu, Headphones, ShieldCheck, Sparkles, Info, PhoneCall, CreditCard,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_likes-telecom-app/artifacts/szvng4fe_IMG_6073.png";
const APP_MOCKUP = "https://static.prod-images.emergentagent.com/jobs/8c47229e-970c-4034-957a-3142574f48e1/images/d9a60518184f6cf887fa916269edbc7b3c2e5b2e7ff680c7b4d73f914366d7a5.jpeg";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (u) => (u && u.startsWith("/") && !u.startsWith("/banners/") ? `${BACKEND}${u}` : u);

const HERO_SLIDES = [
  { url: "https://images.unsplash.com/photo-1758272420990-30a0735e68a1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", alt: "Chica feliz con su móvil" },
  { url: "https://images.unsplash.com/photo-1758525226705-3061215c7445?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", alt: "Amigas viendo el móvil juntas" },
  { url: "https://images.unsplash.com/photo-1758598738092-a7cd486baadd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", alt: "Familia conectada en casa con fibra" },
  { url: "https://images.unsplash.com/photo-1682980094726-f3f9be0abbde?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", alt: "Calle de Madrid, España" },
  { url: "https://images.unsplash.com/photo-1758272420171-6e7de7b36789?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200", alt: "Joven usando su smartphone en la ciudad" },
];

const BLUE = "#015EEF";
const ORANGE = "#FF7A00";

const TAB_META = {
  Mobile: { label: "Móvil", icon: Signal },
  Fiber: { label: "Fibra", icon: Wifi },
  Fixed: { label: "Fibra", icon: Wifi },
  Convergent: { label: "Paquetes", icon: Sparkles },
  M2M: { label: "M2M", icon: Signal },
  PBX: { label: "PBX", icon: Signal },
  TV: { label: "TV", icon: Tv },
  Satellite: { label: "Satélite", icon: Satellite },
  Energy: { label: "Energía", icon: Zap },
  Device: { label: "Dispositivos", icon: Smartphone },
  International: { label: "Internacional", icon: Signal },
  Bonos: { label: "Bonos", icon: Sparkles },
  Paquetes: { label: "Paquetes", icon: Sparkles },
  Other: { label: "Otros", icon: Sparkles },
};

const ICONS = { Repeat, Zap, Smartphone, Headphones, ShieldCheck, Sparkles, Wifi, Signal };

export default function PublicCatalog() {
  const [catalog, setCatalog] = useState({});
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [slide, setSlide] = useState(0);
  const navigate = useNavigate();

  const [cbProduct, setCbProduct] = useState(null);
  const [cbForm, setCbForm] = useState({ name: "", surname: "", phone: "" });
  const [cbBusy, setCbBusy] = useState(false);
  const submitCallback = async (e) => {
    e.preventDefault();
    if (!cbForm.name.trim() || !cbForm.phone.trim()) { toast.error("Indica al menos tu nombre y teléfono"); return; }
    setCbBusy(true);
    try {
      await api.post("/public/callback", {
        productId: cbProduct?.productId, productName: cbProduct?.productName,
        name: cbForm.name.trim(), surname: cbForm.surname.trim(), phone: cbForm.phone.trim(),
      });
      toast.success("¡Solicitud enviada! Te llamaremos lo antes posible.");
      setCbProduct(null); setCbForm({ name: "", surname: "", phone: "" });
    } catch (err) { toast.error(apiErr(err)); } finally { setCbBusy(false); }
  };
  const openCallback = (product = null) => {
    setCbForm({ name: "", surname: "", phone: "" });
    setCbProduct(product || { general: true });
  };

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => s + 1), 4500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.get("/public/catalog").then((r) => {
      setCatalog(r.data);
      const keys = Object.keys(r.data || {}).filter((k) => (r.data[k] || []).length);
      if (keys.length) setTab((t) => t || keys[0]);
    }).catch(() => {});
    api.get("/public/site-content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  // Precarga de banners para que el carrusel no parpadee ni muestre imágenes a medio cargar
  useEffect(() => {
    ((content && content.heroBanners) || [])
      .filter((b) => b && b.url && b.active !== false)
      .forEach((b) => { const im = new Image(); im.src = imgSrc(b.url); });
  }, [content]);

  if (!content) {
    return <div className="min-h-screen grid place-items-center bg-white"><div className="h-10 w-10 rounded-full border-2 border-[#015EEF] border-t-transparent animate-spin" /></div>;
  }

  const TABS = Object.keys(catalog).filter((k) => (catalog[k] || []).length)
    .map((k) => ({ key: k, label: (TAB_META[k] || {}).label || k, icon: (TAB_META[k] || {}).icon || Signal }));

  const { hero, plans, coverage, trust, cities, footer, partners } = content;
  const partnerLogos = (partners || []).filter((p) => p && p.logo && p.active !== false);
  const banners = (content.heroBanners || []).filter((b) => b && b.url && b.active !== false);
  const openBanner = (link) => {
    if (!link) return;
    if (link.startsWith("http")) window.open(link, "_blank");
    else navigate(link);
  };
  const hasBanners = banners.length > 0;
  const heroImages = hasBanners
    ? banners.map((b) => ({ url: imgSrc(b.url), alt: "GoRoky", link: b.link }))
    : HERO_SLIDES;

  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] font-body selection:bg-[#FF7A00] selection:text-white" data-testid="public-catalog">
      {/* Header — crystal glass */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="#top" className="flex items-center" data-testid="header-logo"><img src={LOGO} alt="GoRoky · roky móvil" className="h-9 w-auto" /></a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-bold">
            <a href="#planes" className="text-slate-800 hover:text-[#015EEF] transition-colors tracking-wide">Tarifas</a>
            <a href="#cobertura" className="text-slate-800 hover:text-[#015EEF] transition-colors tracking-wide">Cobertura fibra</a>
            <button onClick={() => openCallback()} data-testid="header-callback-btn"
              className="inline-flex items-center gap-2 text-slate-800 hover:text-[#FF7A00] transition-colors tracking-wide">
              <PhoneCall size={16} /> Te llamamos
            </button>
            <button onClick={() => navigate("/pagar-cuenta")} data-testid="header-paybill-btn"
              className="inline-flex items-center gap-2 text-slate-800 hover:text-[#FF7A00] transition-colors tracking-wide">
              <CreditCard size={16} /> Pagar cuenta
            </button>
            <button onClick={() => navigate("/login")} data-testid="header-login-btn"
              className="rounded-full border-2 border-[#015EEF] text-[#015EEF] hover:bg-[#015EEF] hover:text-white font-bold px-6 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A00] focus-visible:ring-offset-2">Mi cuenta</button>
          </nav>
          {/* Mobile menu */}
          <div className="md:hidden">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" aria-label="Menú" className="grid place-items-center h-11 w-11 rounded-full bg-slate-100 active:scale-95 transition-transform"><Menu size={22} /></button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 flex flex-col gap-6 pt-10">
                <img src={LOGO} alt="GoRoky" className="h-9 w-auto" />
                <a href="#planes" onClick={() => setMenuOpen(false)} className="text-lg font-bold text-slate-800">Tarifas</a>
                <a href="#cobertura" onClick={() => setMenuOpen(false)} className="text-lg font-bold text-slate-800">Cobertura fibra</a>
                <button onClick={() => { setMenuOpen(false); openCallback(); }} data-testid="mobile-callback-btn"
                  className="inline-flex items-center gap-2 text-lg font-bold text-slate-800 text-left">
                  <PhoneCall size={18} className="text-[#FF7A00]" /> Te llamamos
                </button>
                <button onClick={() => { setMenuOpen(false); navigate("/pagar-cuenta"); }} data-testid="mobile-paybill-btn"
                  className="inline-flex items-center gap-2 text-lg font-bold text-slate-800 text-left">
                  <CreditCard size={18} className="text-[#FF7A00]" /> Pagar cuenta
                </button>
                <button onClick={() => { setMenuOpen(false); navigate("/login"); }} data-testid="mobile-login-btn"
                  className="rounded-full bg-[#015EEF] text-white font-bold px-6 py-3 mt-2">Mi cuenta</button>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero — texto a la izquierda + banner/imagen a la derecha */}
      <section id="top" className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-[#015EEF]/5 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#FF7A00]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center pt-32 lg:pt-36 pb-16 lg:pb-24">
          <motion.div initial="hidden" animate="show"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
            className="lg:col-span-6">
            <motion.span variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] font-bold uppercase text-[#FF7A00] bg-[#FF7A00]/10 rounded-full px-4 py-2 mb-6">
              <Star size={14} className="fill-[#FF7A00]" /> {hero.badge}
            </motion.span>
            <motion.h1 variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              {hero.title} <span className="text-[#015EEF]">{hero.titleHighlight}</span>
            </motion.h1>
            <motion.p variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
              className="text-slate-600 mt-6 text-lg sm:text-xl leading-relaxed max-w-xl">{hero.subtitle}</motion.p>
            <motion.div variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
              className="flex flex-col sm:flex-row gap-4 mt-9">
              <a href="#planes" data-testid="hero-cta-planes"
                className="rounded-full bg-[#015EEF] hover:bg-[#004cc7] text-white font-bold px-8 py-4 inline-flex items-center justify-center gap-2 transition-[transform,box-shadow,background-color] hover:-translate-y-1 shadow-[0_10px_30px_rgba(1,94,239,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7A00] focus-visible:ring-offset-2">
                {hero.ctaPrimary} <ArrowRight size={18} />
              </a>
              <a href="#cobertura" data-testid="hero-cta-cobertura"
                className="rounded-full bg-[#FF7A00] hover:bg-[#e66e00] text-white font-bold px-8 py-4 inline-flex items-center justify-center gap-2 transition-[transform,box-shadow,background-color] hover:-translate-y-1 shadow-[0_10px_30px_rgba(255,122,0,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#015EEF] focus-visible:ring-offset-2">
                {hero.ctaSecondary}
              </a>
            </motion.div>
            <motion.div variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0 } }}
              className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-10 text-sm text-slate-500">
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF7A00]" /> Alta 100% online</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF7A00]" /> Cobertura nacional</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-[#FF7A00]" /> Red 4G/5G</span>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-6 relative">
            {/* Banner/carrusel de imágenes */}
            <div className={`relative rounded-[2.5rem] overflow-hidden shadow-2xl bg-slate-100 ${hasBanners ? "aspect-square ring-1 ring-black/5" : "aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5]"}`}>
              <AnimatePresence mode="wait">
                <motion.img key={slide % heroImages.length} src={heroImages[slide % heroImages.length].url} alt={heroImages[slide % heroImages.length].alt || "GoRoky"}
                  onClick={() => heroImages[slide % heroImages.length].link && openBanner(heroImages[slide % heroImages.length].link)}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.4, ease: "easeInOut" }}
                  className={`absolute inset-0 w-full h-full object-cover ${heroImages[slide % heroImages.length].link ? "cursor-pointer" : ""}`} data-testid="hero-carousel-img" />
              </AnimatePresence>
              {!hasBanners && <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent pointer-events-none" />}
              {/* Puntitos */}
              {heroImages.length > 1 && (
                <div className="absolute bottom-5 right-5 flex gap-1.5">
                  {heroImages.map((_, i) => (
                    <button key={i} aria-label={`Imagen ${i + 1}`} onClick={() => setSlide(i)} data-testid={`hero-dot-${i}`}
                      className={`h-2 rounded-full transition-all ${i === slide % heroImages.length ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`} />
                  ))}
                </div>
              )}
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="absolute -bottom-5 -left-2 sm:left-6 bg-white text-[#0A0A0A] rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3 border border-slate-100">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-[#015EEF]/10 text-[#015EEF]"><Repeat size={20} /></span>
              <div><p className="font-heading font-bold leading-tight">Portabilidad gratis</p><p className="text-sm text-slate-500">Conserva tu número</p></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Planes */}
      <section id="planes" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <p className="text-xs tracking-[0.2em] font-bold uppercase text-[#FF7A00] mb-3">{plans.eyebrow}</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{plans.title}</h2>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="inline-flex flex-wrap h-auto gap-1 bg-slate-200/60 p-1.5 rounded-full mb-12">
            {TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`public-tab-${t.key}`}
                className="gap-1.5 rounded-full px-6 py-2.5 text-sm font-bold transition-colors data-[state=active]:bg-white data-[state=active]:text-[#015EEF] data-[state=active]:shadow-sm text-slate-500 hover:text-slate-900">
                <t.icon size={15} /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
                {(catalog[t.key] || []).map((p, i) => {
                  const list = catalog[t.key] || [];
                  const anyPopular = list.some((x) => x.popular);
                  const popular = anyPopular ? !!p.popular : (i === 1 && list.length >= 3);
                  return (
                    <motion.div key={p.productId} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                      data-testid={`public-product-${p.productId}`}
                      className={popular
                        ? "relative bg-[#015EEF] text-white rounded-[2rem] shadow-[0_24px_50px_rgba(1,94,239,0.35)] p-8 flex flex-col lg:scale-105 z-10 overflow-hidden"
                        : "relative bg-white rounded-[2rem] border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_24px_45px_rgb(0,0,0,0.1)] hover:-translate-y-2 transition-[transform,box-shadow] duration-300 p-8 flex flex-col"}>
                      {popular && (
                        <>
                          <div className="pointer-events-none absolute -top-16 -right-16 h-52 w-52 rounded-full bg-white/10 blur-2xl" />
                          <span className="absolute top-0 right-0 bg-[#FF7A00] text-white text-xs font-bold uppercase tracking-wider py-1.5 px-4 rounded-bl-2xl rounded-tr-[2rem]">Más popular</span>
                        </>
                      )}
                      <span className={`grid place-items-center h-12 w-12 rounded-2xl mb-5 ${popular ? "bg-white/15 text-white" : "bg-[#015EEF]/10 text-[#015EEF]"}`}><t.icon size={24} /></span>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-heading font-bold text-2xl leading-tight ${popular ? "text-white" : ""}`}>{p.productName}</h3>
                        {(p.marketingText || []).length > 0 && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <button aria-label="Ver detalle del servicio" data-testid={`detail-${p.productId}`}
                                className={`shrink-0 grid place-items-center h-8 w-8 rounded-full border transition-colors ${popular ? "border-white/30 text-white hover:bg-white/10" : "border-slate-200 text-slate-400 hover:text-[#015EEF] hover:border-[#015EEF]"}`}>
                                <Info size={16} />
                              </button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md">
                              <DialogHeader><DialogTitle>Detalle · {p.productName}</DialogTitle></DialogHeader>
                              <ul className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                                {(p.marketingText || []).map((m, j) => (
                                  <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600">
                                    <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                                    <span>{m.title ? <b className="text-slate-900 font-semibold">{m.title}: </b> : null}{m.value}</span>
                                  </li>
                                ))}
                              </ul>
                              <button className="mt-2 w-full rounded-full py-3 bg-[#015EEF] hover:bg-[#004cc7] text-white font-bold inline-flex items-center justify-center gap-2 transition-colors"
                                onClick={() => navigate(`/contratar/${p.productId}`)} data-testid={`detail-contract-${p.productId}`}>
                                Contratar <ArrowRight size={16} />
                              </button>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                      <div className="mt-3 mb-6 flex items-end gap-1">
                        <span className="font-heading text-6xl font-black tracking-tighter leading-none">{p.price.toFixed(2).replace(".", ",")}</span>
                        <span className={`text-lg mb-1.5 ${popular ? "text-white/70" : "text-slate-400"}`}>€/mes</span>
                      </div>
                      <ul className="space-y-3 mb-7 flex-1">
                        {(p.marketingText || []).slice(0, 3).map((m, j) => (
                          <li key={j} className={`flex items-start gap-2.5 text-sm ${popular ? "text-white/90" : "text-slate-600"}`}>
                            <CheckCircle2 size={18} className={`shrink-0 mt-0.5 ${popular ? "text-white" : "text-emerald-500"}`} />
                            <span className="line-clamp-2">{m.title ? <b className={popular ? "text-white font-semibold" : "text-slate-900 font-semibold"}>{m.title}: </b> : null}{m.value}</span>
                          </li>
                        ))}
                        {(p.marketingText || []).length > 3 && (
                          <li>
                            <Dialog>
                              <DialogTrigger asChild>
                                <button data-testid={`more-${p.productId}`}
                                  className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${popular ? "text-white/90 hover:text-white" : "text-[#015EEF] hover:text-[#004cc7]"}`}>
                                  <Info size={15} /> Ver todo el detalle
                                </button>
                              </DialogTrigger>
                              <DialogContent className="max-w-md">
                                <DialogHeader><DialogTitle>Detalle · {p.productName}</DialogTitle></DialogHeader>
                                <ul className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
                                  {(p.marketingText || []).map((m, j) => (
                                    <li key={j} className="flex items-start gap-2.5 text-sm text-slate-600">
                                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                                      <span>{m.title ? <b className="text-slate-900 font-semibold">{m.title}: </b> : null}{m.value}</span>
                                    </li>
                                  ))}
                                </ul>
                              </DialogContent>
                            </Dialog>
                          </li>
                        )}
                      </ul>
                      {p.channels?.length > 0 && (
                        <Dialog>
                          <DialogTrigger asChild>
                            <button className={`w-full rounded-full py-2.5 mb-3 border font-semibold text-sm inline-flex items-center justify-center gap-1.5 transition-colors ${popular ? "border-white/30 text-white hover:bg-white/10" : "border-slate-200 hover:border-[#015EEF]"}`} data-testid={`channels-${p.productId}`}>
                              <Tv2 size={15} /> Ver {p.channels.length} canales
                            </button>
                          </DialogTrigger>
                          <DialogContent className="max-w-md">
                            <DialogHeader><DialogTitle>Canales · {p.productName}</DialogTitle></DialogHeader>
                            <div className="grid grid-cols-2 gap-2 max-h-80 overflow-y-auto">
                              {p.channels.map((ch, k) => <span key={k} className="flex items-center gap-1.5 text-sm py-1"><Tv size={13} className="text-[#015EEF] shrink-0" /> {ch}</span>)}
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                      <button
                        className={`w-full rounded-full py-3.5 font-bold inline-flex items-center justify-center gap-2 transition-[transform,box-shadow,background-color] hover:-translate-y-0.5 ${popular ? "bg-[#FF7A00] hover:bg-[#e66e00] text-white shadow-[0_10px_28px_rgba(255,122,0,0.45)]" : "bg-[#015EEF] hover:bg-[#004cc7] text-white shadow-[0_10px_28px_rgba(1,94,239,0.3)]"}`}
                        data-testid={`contract-${p.productId}`} onClick={() => navigate(`/contratar/${p.productId}`)}>
                        Contratar <ArrowRight size={16} />
                      </button>
                      <button
                        className="mt-2 w-full rounded-full py-2 text-sm font-semibold inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-600 hover:border-[#015EEF] hover:text-[#015EEF] transition-colors"
                        data-testid={`callback-${p.productId}`}
                        onClick={() => { setCbForm({ name: "", surname: "", phone: "" }); setCbProduct(p); }}>
                        <PhoneCall size={14} /> Te llamamos
                      </button>
                    </motion.div>
                  );
                })}
                {(catalog[t.key] || []).length === 0 && <p className="text-slate-500 col-span-full text-center py-16">No hay tarifas disponibles en esta categoría.</p>}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* Cobertura fibra — azul */}
      <section id="cobertura" className="relative overflow-hidden bg-[#015EEF] text-white">
        <div className="pointer-events-none absolute -top-24 -left-24 h-[420px] w-[420px] rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 right-0 h-[420px] w-[420px] rounded-full bg-[#FF7A00]/25 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <p className="text-xs tracking-[0.2em] font-bold uppercase text-[#FFD8B0] mb-3">{coverage.eyebrow}</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{coverage.title}</h2>
            <p className="text-white/85 mt-5 text-lg max-w-md leading-relaxed">{coverage.description}</p>
            <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8 text-sm text-white/85">
              <span className="inline-flex items-center gap-2"><Wifi size={16} className="text-[#FFB877]" /> FTTH hasta 1&nbsp;Gb</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck size={16} className="text-[#FFB877]" /> Instalación profesional</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white rounded-[2rem] p-6 sm:p-7 shadow-2xl">
            <CoverageChecker />
          </motion.div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {(trust || []).map((f, i) => {
            const Icon = ICONS[f.icon] || Sparkles;
            return (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
                className="rounded-[2rem] bg-slate-50 border border-slate-100 p-8 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgb(0,0,0,0.06)] transition-[transform,box-shadow] duration-300">
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-[#FF7A00]/10 text-[#FF7A00] mb-5"><Icon size={26} /></span>
                <h3 className="font-heading font-bold text-xl">{f.title}</h3>
                <p className="text-slate-500 mt-2 leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* App móvil — sección moderna */}
      <section className="relative overflow-hidden bg-[#05070D] text-white py-24 lg:py-32" data-testid="app-section" id="app">
        <div className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full bg-[#015EEF]/30 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -right-24 h-[480px] w-[480px] rounded-full bg-[#FF7A00]/20 blur-[120px]" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/15 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-[#7FB0FF] backdrop-blur">
              <Smartphone size={14} /> App GoRoky
            </span>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              Controla toda tu línea desde tu propia <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#4D93FF] to-[#FF7A00]">aplicación</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 max-w-md leading-relaxed">
              Consulta tus gigas en tiempo real, revisa tus facturas y gestiona todas tus líneas desde el móvil. Tu operador, en tu bolsillo.
            </p>
            <ul className="mt-8 space-y-3">
              {["Consumo de datos en tiempo real", "Tus facturas siempre a mano", "Gestiona y activa tus líneas"].map((f) => (
                <li key={f} className="flex items-center gap-3 text-slate-200">
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-[#015EEF]/30 ring-1 ring-[#4D93FF]/40"><CheckCircle2 size={15} className="text-[#7FB0FF]" /></span>
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button onClick={() => navigate("/login")} data-testid="app-cta-cuenta"
                className="rounded-full bg-white text-[#05070D] font-bold px-8 py-4 inline-flex items-center justify-center gap-2 transition-[transform,box-shadow] hover:-translate-y-1 shadow-[0_10px_40px_rgba(255,255,255,0.15)]">
                Acceder a Mi cuenta <ArrowRight size={18} />
              </button>
              <div className="flex items-center gap-2 text-sm text-slate-400 justify-center sm:justify-start">
                <span className="inline-flex h-2 w-2 rounded-full bg-[#FF7A00] animate-pulse" /> App para iOS y Android próximamente
              </div>
            </div>
          </motion.div>

          <motion.div className="relative flex justify-center lg:justify-end"
            initial={{ opacity: 0, scale: 0.9, y: 40 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
            <motion.img src={APP_MOCKUP} alt="App GoRoky en un smartphone" data-testid="app-mockup-img"
              animate={{ y: [0, -14, 0] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10 w-[300px] sm:w-[360px] lg:w-[430px] h-auto rounded-3xl" />
          </motion.div>
        </div>
      </section>

      {/* Marquee ciudades (SEO) */}
      <section className="border-y border-slate-100 bg-white overflow-hidden py-8" aria-label="Cobertura por ciudades">
        <motion.div className="flex gap-14 whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }} transition={{ duration: 34, repeat: Infinity, ease: "linear" }}>
          {[...(cities || []), ...(cities || [])].map((c, i) => (
            <span key={i} className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-transparent inline-flex items-center gap-4"
              style={{ WebkitTextStroke: "1.5px rgba(1,94,239,0.2)" }}>
              Fibra y móvil en {c} <span className="text-[#FF7A00]" style={{ WebkitTextStroke: "0" }}>•</span>
            </span>
          ))}
        </motion.div>
      </section>

      {/* Red y partners */}
      {partnerLogos.length > 0 && (
        <section className="bg-white border-t border-slate-100 py-14" aria-label="Nuestra red y partners" data-testid="partners-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-8">Navegamos con la mejor red</p>
            <div className="flex flex-wrap items-center justify-center gap-x-14 gap-y-8">
              {partnerLogos.map((p, i) => (
                <img key={i} src={imgSrc(p.logo)} alt={p.name || "Partner"} title={p.name || ""}
                  data-testid={`partner-logo-${i}`}
                  className="h-14 sm:h-16 w-auto object-contain hover:scale-105 transition-transform duration-300" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer SEO */}
      <footer className="bg-[#0A0A0A] text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row justify-between gap-12">
            <div className="max-w-md">
              <img src={LOGO} alt="GoRoky" className="h-9 w-auto bg-white rounded-xl p-1.5 mb-5" />
              <p className="text-slate-400 leading-relaxed">{footer.description}</p>
            </div>
            <div>
              <p className="font-heading font-bold text-white mb-4">Cobertura por ciudades</p>
              <div className="grid grid-cols-2 gap-x-10 gap-y-2 text-slate-400 text-sm">
                {(cities || []).map((c) => <span key={c}>Fibra en {c}</span>)}
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 mt-12 pt-8 text-sm text-slate-500 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-semibold text-slate-300">GoRoky · RokyMovil · Tramilex</span>
              <span>© {new Date().getFullYear()} {footer.company}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <a href="/privacidad" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Política de privacidad</a>
              <a href="/terminos" className="hover:text-white transition-colors" data-testid="footer-terms-link">Términos y condiciones</a>
              <button onClick={() => navigate("/login")} className="hover:text-white transition-colors text-left" data-testid="footer-login-btn">Acceso clientes</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Formulario "Te llamamos" */}
      <Dialog open={!!cbProduct} onOpenChange={(o) => { if (!o) setCbProduct(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="callback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><PhoneCall size={18} className="text-[#015EEF]" /> Te llamamos</DialogTitle>
            <DialogDescription>
              {cbProduct?.productName
                ? <>Déjanos tus datos y te llamamos para informarte sobre <b>{cbProduct.productName}</b>.</>
                : <>Déjanos tus datos y te llamamos para informarte sobre <b>nuestras tarifas de móvil y fibra</b>.</>}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCallback} className="space-y-3 pt-1">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Nombre</label>
                <input data-testid="callback-name" value={cbForm.name} onChange={(e) => setCbForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#015EEF]/40 focus:border-[#015EEF]" placeholder="Tu nombre" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Apellido</label>
                <input data-testid="callback-surname" value={cbForm.surname} onChange={(e) => setCbForm((f) => ({ ...f, surname: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#015EEF]/40 focus:border-[#015EEF]" placeholder="Tu apellido" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Teléfono</label>
              <input data-testid="callback-phone" type="tel" value={cbForm.phone} onChange={(e) => setCbForm((f) => ({ ...f, phone: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#015EEF]/40 focus:border-[#015EEF]" placeholder="Ej. 600 123 456" />
            </div>
            <button type="submit" disabled={cbBusy} data-testid="callback-submit"
              className="w-full rounded-full bg-[#015EEF] hover:bg-[#004cc7] text-white font-bold py-3 inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {cbBusy ? "Enviando…" : "Solicitar llamada"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
